import React, { useState, useEffect, useCallback } from "react";
import { api } from "../../api";
import { Icon, Modal, Empty, Spinner, useToast } from "../../components/ui";

const ROLE_LABEL = { SUPER_ADMIN: "Super admin", ADMIN: "Admin", SUPERVISOR: "Supervisor", USER: "Production" };

function ago(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60); if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h} hr ago`;
  return `${Math.floor(h / 24)} d ago`;
}
function when(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function Sessions() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState("");
  const [revoking, setRevoking] = useState(null); // the session pending confirm

  const load = useCallback(async (silent) => {
    try { setRows(await api.listSessions(silent ? { silent: true } : undefined)); setErr(""); }
    catch (e) { setErr(e.message); if (!silent) setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // auto-refresh every 20s so "last active" stays accurate (silent — no overlay flash on an untouched screen)
  useEffect(() => { const t = setInterval(() => load(true), 20000); return () => clearInterval(t); }, [load]);

  const doRevoke = async (s) => {
    setRevoking(null);
    try { await api.revokeSession(s.id); toast(`${s.userName} signed out`, "ok"); load(); }
    catch (e) { toast(e.message, "err"); }
  };

  if (rows === null) return <div className="empty"><Spinner /></div>;

  return (
    <div className="sess-wrap">
      <div className="toolbar">
        <div><h2 style={{ margin: 0 }}>Active sessions</h2>
          <div className="cell-sub">{rows.length} device{rows.length === 1 ? "" : "s"} currently signed in</div></div>
        <button className="btn btn-ghost" onClick={load}>Refresh</button>
      </div>
      {err && <div className="import-err"><Icon.x width="15" height="15" /><span>{err}</span></div>}
      {rows.length === 0 ? <Empty title="No active sessions" sub="No one is signed in right now." /> : (
        <div className="card"><div className="table-wrap">
          <table className="tbl sess-table">
            <colgroup><col style={{ width: "260px" }} /><col style={{ width: "120px" }} /><col style={{ width: "150px" }} /><col style={{ width: "160px" }} /><col style={{ width: "150px" }} /><col style={{ width: "120px" }} /></colgroup>
            <thead><tr>
              <th>User</th><th>Role</th><th>IP address</th><th>Device</th><th>Last active</th><th style={{ textAlign: "right" }}>Action</th>
            </tr></thead>
            <tbody>
              {rows.map((s) => (
                <tr key={s.id}>
                  <td><div className="cell-strong">{s.userName}</div><div className="cell-sub">{s.userEmail}</div></td>
                  <td>{ROLE_LABEL[s.userRole] || s.userRole}</td>
                  <td className="mono">{s.ip || "—"}</td>
                  <td>{s.device || "—"}</td>
                  <td><span title={when(s.lastSeenAt)}>{ago(s.lastSeenAt)}</span></td>
                  <td style={{ textAlign: "right" }}>
                    <button className="btn btn-sm btn-danger" onClick={() => setRevoking(s)}>Revoke</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div></div>
      )}

      {revoking && (
        <Modal title="Sign this user out?" onClose={() => setRevoking(null)}
          foot={<><button className="btn btn-ghost" onClick={() => setRevoking(null)}>Cancel</button>
            <button className="btn btn-danger" onClick={() => doRevoke(revoking)}>Yes, sign out</button></>}>
          <p style={{ margin: 0, lineHeight: 1.6 }}>
            This will immediately sign <b>{revoking.userName}</b> out on <b>{revoking.device}</b> (IP {revoking.ip || "unknown"}).
            They'll need to log in again to keep using the tool.
          </p>
        </Modal>
      )}
    </div>
  );
}